// Legal acceptance (F8): which terms are in force (GET /v1/legal, public), whether the signed-in person has accepted
// them (GET /v1/me/legal) and the accept call (POST /v1/me/legal/accept). Sign-up sends the terms version it was
// shown (`acceptTerms`); checkout and credit packs answer 409 `terms_required` until the person has accepted. Only the
// person can accept: an API key reads what its person accepted, and the accept call refuses it.
// Everything here is pure or takes the api, so it can be tested. The mock lives in ./mock/legal.ts.
import { type Api, ApiError } from "../api.ts";
import { mocksOn } from "./flags.ts";
import { loadWithFallback } from "./loadWithFallback.ts";

export interface LegalDoc {
  version: string;
  url: string;
}
export interface LegalDocs {
  terms: LegalDoc;
  privacy: LegalDoc;
  cookies: LegalDoc;
  dpa: LegalDoc;
}

/** What stands when the server does not answer (or is older than F8's legal routes): the launch terms. */
export const FALLBACK_LEGAL: LegalDocs = {
  terms: { version: "2026-10-26", url: "https://www.filmsquare.ai/terms" },
  privacy: { version: "2026-10-26", url: "https://www.filmsquare.ai/privacy" },
  cookies: { version: "2026-10-26", url: "https://www.filmsquare.ai/cookies" },
  dpa: { version: "2026-10-26", url: "https://www.filmsquare.ai/dpa" },
};

let current: LegalDocs = FALLBACK_LEGAL;
export const getLegal = (): LegalDocs => current;
export const setLegal = (l: LegalDocs): void => {
  current = l;
};

/** A document's address: https only (the server is trusted, but a link in the page should never run script). */
const safeUrl = (u: unknown, fallback: string): string => {
  if (typeof u !== "string") return fallback;
  try {
    return new URL(u).protocol === "https:" ? u : fallback;
  } catch {
    return fallback;
  }
};

const doc = (raw: unknown, fallback: LegalDoc): LegalDoc => {
  const r = (raw ?? {}) as { version?: unknown; url?: unknown };
  return {
    version: typeof r.version === "string" && r.version ? r.version : fallback.version,
    url: safeUrl(r.url, fallback.url),
  };
};

/** The body of GET /legal. Null when it is not an object at all; a missing document keeps its fallback. */
export function parseLegal(raw: unknown): LegalDocs | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    terms: doc(r.terms, FALLBACK_LEGAL.terms),
    privacy: doc(r.privacy, FALLBACK_LEGAL.privacy),
    cookies: doc(r.cookies, FALLBACK_LEGAL.cookies),
    dpa: doc(r.dpa, FALLBACK_LEGAL.dpa),
  };
}

/**
 * Loads the terms in force. Never rejects and never waits longer than `timeoutMs`; when the server does not answer
 * the fallback stays. Answers what is in force afterwards.
 */
export function loadLegal(request: () => Promise<unknown>, timeoutMs = 3000): Promise<LegalDocs> {
  return loadWithFallback(request, parseLegal, getLegal, setLegal, "legal", timeoutMs);
}

/** A refused sign-up names the terms version in force; take it up so that the next try sends it. */
export function adoptTermsVersion(details: unknown): void {
  const d = (details ?? {}) as { version?: unknown; url?: unknown };
  if (typeof d.version !== "string" || !d.version) return;
  current = { ...current, terms: { version: d.version, url: safeUrl(d.url, current.terms.url) } };
}

// ── the signed-in person's acceptance ─────────────────────────────────────────────────────────────────────

export interface LegalStatus {
  /** the terms version in force */
  termsVersion: string;
  acceptedVersion: string | null;
  acceptedAt: string | null;
  needsAcceptance: boolean;
}

/** The body of GET /me/legal (and of the accept call). Null when it does not say: then there is nothing to accept. */
export function parseStatus(raw: unknown): LegalStatus | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.needs_acceptance !== "boolean") return null;
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  return {
    termsVersion: str(r.terms_version) ?? current.terms.version,
    acceptedVersion: str(r.accepted_version),
    acceptedAt: str(r.accepted_at),
    needsAcceptance: r.needs_acceptance,
  };
}

export interface LegalSource {
  /** Null: this server has no legal routes, so nothing is asked. */
  status(): Promise<LegalStatus | null>;
  /** Accepts the terms of that version; answers the new status. 409 terms_version_outdated when it is not current. */
  accept(version: string): Promise<LegalStatus>;
}

/** A server without the route (404, 501) has nothing to accept. */
const isAbsent = (e: unknown): boolean => e instanceof ApiError && (e.status === 404 || e.status === 501);

export function createLegalSource(api: Api): LegalSource {
  return {
    status: async () => {
      try {
        return parseStatus(await api.request("GET", "/me/legal"));
      } catch (e) {
        if (isAbsent(e)) return null;
        throw e;
      }
    },
    accept: async (version) => {
      const s = parseStatus(await api.request("POST", "/me/legal/accept", { body: { version } }));
      if (!s) throw new Error("The server did not confirm. Try again in a moment.");
      return s;
    },
  };
}

/** `?mocklegal=pending` shows the acceptance dialog in mock mode. */
export const mockLegalPending = (search: string = window.location.search): boolean =>
  new URLSearchParams(search).get("mocklegal") === "pending";

/** The real source, or with the mock flag on the mock (accepted, unless `?mocklegal=pending`). */
export async function legalSource(api: Api): Promise<LegalSource> {
  if (!mocksOn()) return createLegalSource(api);
  const { createMockLegal } = await import("./mock/legal.ts");
  return createMockLegal(mockLegalPending());
}

// ── the answers that mean "accept the terms first" ────────────────────────────────────────────────────────

/** A 409 `terms_required` (checkout, credit packs). */
export const isTermsRequired = (e: unknown): boolean =>
  e instanceof ApiError && e.status === 409 && e.code === "terms_required";

/** A 409 `terms_version_outdated` (the accept call named a version that is no longer current). */
export const isTermsOutdated = (e: unknown): boolean =>
  e instanceof ApiError && e.status === 409 && e.code === "terms_version_outdated";

export const TERMS_REQUIRED_TEXT = "Accept the Terms of Service to continue.";
export const TERMS_KEY_TEXT =
  "An API key cannot accept the Terms of Service. Sign in with your email to accept them, then try again.";

/**
 * Runs `run`; when it is refused with `terms_required`, asks the person (`ask` shows the dialog and answers whether
 * they accepted) and, once they have, runs it again. Declined: the refusal stands. Any other error passes through.
 */
export async function retryAfterTerms<T>(run: () => Promise<T>, ask: () => Promise<boolean>): Promise<T> {
  try {
    return await run();
  } catch (e) {
    if (!isTermsRequired(e) || !(await ask())) throw e;
    return run();
  }
}

/** The `ask` for a session on an API key: the dialog could only fail, so the refusal says what can be done instead. */
export const keyCannotAccept = (): Promise<boolean> => Promise.reject(new Error(TERMS_KEY_TEXT));
